import React from "react";
import {
  Badge,
  Box,
  Flex,
  HStack,
  Text,
  VStack,
  Tooltip,
} from "@chakra-ui/react";
import { BiStore } from "react-icons/bi";
import { DeliveryOption } from "../../types";
import {
  formatCPTRange,
  formatGaraazSlotRangeLabel,
  parseGaraazSlotNumber,
} from "../../utils/deliveryTimeUtils";
import { calculateDiscountedPrice } from "../../utils/deliveryCostUtils";
import { getEffectiveDeliveryCost } from "../../utils/vehicleTypeUtils";
import {
  getCptSlabBadgeAbbreviation,
  getCptSlabBadgeColorScheme,
  getCptSlabFullLabel,
  normalizeCptSlabValue,
  shouldShowLeftSlabBadge,
} from "../../utils/deliveryOptionCptSlabUtils";
import { DeleteOptionControl } from "./DeleteOptionControl";
import { DeliveryOptionCptAuditInfo } from "./DeliveryOptionCptAuditInfo";
import { shouldShowCptAuditInfo } from "../../utils/cptAuditTooltipUtils";
import { CPTSlabForManualOption } from "../../../../views/Orders/enums";
import {
  DELETE_COLUMN_WIDTH,
  OPTION_ROW_OUTER_GAP,
  getDeliveryOptionCardStyles,
} from "../constants/deliveryOptionRowLayout";

export interface DeliveryOptionRowProps {
  option: DeliveryOption;
  isSelected: boolean;
  isExpired: boolean;
  isRemainingItems: boolean;
  onSelect: () => void;
  onDelete: (optionId: string) => Promise<void>;
  isDeleteDisabled: boolean;
  deleteDisabledTooltip?: string;
  isDeletingOption: boolean;
  isReadOnly?: boolean;
  /** When false, hides the delete control (e.g. remaining-items / read-only). */
  canDelete?: boolean;
  supplierOptions?: Array<{
    supplierId: string;
    businessName: string;
    branchName?: string | null;
    branchCode?: string | null;
    isAssociated: boolean;
    address?: any;
  }>;
  selectedSupplierId?: string;
  onSupplierChange?: (supplierId: string) => void;
  selectedVehicleType?: string;
}

export const DeliveryOptionRow: React.FC<DeliveryOptionRowProps> = ({
  option,
  isSelected,
  isExpired,
  isRemainingItems,
  onSelect,
  onDelete,
  isDeleteDisabled,
  deleteDisabledTooltip = "Cannot delete while a change is in progress",
  isDeletingOption,
  isReadOnly = false,
  canDelete = true,
  supplierOptions,
  selectedSupplierId,
  onSupplierChange,
  selectedVehicleType,
}) => {
  const slabBadgeAbbreviation = getCptSlabBadgeAbbreviation(option);
  const slabTooltipLabel = getCptSlabFullLabel(option);
  const normalizedCptSlab = normalizeCptSlabValue(option.cptSlab);
  const showLeftSlabBadge =
    shouldShowLeftSlabBadge(option) && slabBadgeAbbreviation != null;
  const leftSlabBadgeColors = getCptSlabBadgeColorScheme(normalizedCptSlab);

  const isInteractionDisabled = isExpired || isRemainingItems || isReadOnly;
  const cardStyles = getDeliveryOptionCardStyles({
    isSelected,
    isInteractionDisabled,
    isReadOnly,
  });

  const defaultSupplierId = React.useMemo(() => {
    const splitSupplier = supplierOptions?.find((opt) => !opt.isAssociated);
    if (splitSupplier) return splitSupplier.supplierId;
    return supplierOptions?.[0]?.supplierId || "";
  }, [supplierOptions]);

  const activeSupplierId = selectedSupplierId || defaultSupplierId;

  const isDifferentSupplier = React.useMemo(() => {
    if (!activeSupplierId || !defaultSupplierId) return false;
    return activeSupplierId !== defaultSupplierId;
  }, [activeSupplierId, defaultSupplierId]);

  const selectedSupplierName = React.useMemo(() => {
    const selectedId = activeSupplierId || option.supplierId;
    if (!selectedId) return "";
    const found = supplierOptions?.find((opt) => opt.supplierId === selectedId);
    if (found) {
      return (
        found.businessName + (found.branchName ? ` (${found.branchName})` : "")
      );
    }
    return "";
  }, [activeSupplierId, option.supplierId, supplierOptions]);

  const minSlot = parseGaraazSlotNumber(option.minGaraazSlot);
  const maxSlot = parseGaraazSlotNumber(option.maxGaraazSlot) ?? minSlot;
  const timeLabel = minSlot
    ? formatGaraazSlotRangeLabel(
        option.cptMin,
        option.cptMax,
        minSlot,
        maxSlot ?? minSlot,
      )
    : formatCPTRange(option.cptMin, option.cptMax);
  const effectiveDeliveryCost = getEffectiveDeliveryCost(
    option,
    selectedVehicleType,
  );

  return (
    <HStack spacing={OPTION_ROW_OUTER_GAP} align="center" w="100%">
      <HStack
        flex={1}
        spacing={4}
        {...cardStyles}
        onClick={() => !isInteractionDisabled && !isSelected && onSelect()}
        position="relative"
      >
        <VStack align="stretch" w="100%" spacing={3}>
          <Flex w="100%" align="center">
            <Box flex="1" display="flex" alignItems="center" minW="220px">
              <HStack spacing={2}>
                <Text
                  fontSize="0.85rem"
                  color="gray.700"
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="600"
                >
                  {timeLabel}
                </Text>
                {shouldShowCptAuditInfo(option) && (
                  <DeliveryOptionCptAuditInfo option={option} />
                )}
                {option.isSystemGenerated === false && (
                  <Badge
                    bg="purple.100"
                    color="purple.700"
                    fontSize="0.65rem"
                    fontWeight="800"
                    px={2}
                    py={0.5}
                    borderRadius="md"
                    textTransform="uppercase"
                  >
                    MANUAL
                  </Badge>
                )}
                {showLeftSlabBadge && (
                  <Tooltip label={slabTooltipLabel} hasArrow placement="top">
                    <Badge
                      bg={leftSlabBadgeColors.bg}
                      color={leftSlabBadgeColors.color}
                      fontSize="0.65rem"
                      fontWeight="900"
                      px={2}
                      h="20px"
                      minH="20px"
                      display="inline-flex"
                      alignItems="center"
                      justifyContent="center"
                      borderRadius="md"
                      textTransform="none"
                      cursor="pointer"
                      lineHeight="1"
                    >
                      {slabBadgeAbbreviation}
                    </Badge>
                  </Tooltip>
                )}
                {selectedSupplierName && isDifferentSupplier && (
                  <Tooltip
                    label={`Pickup Point: ${selectedSupplierName}`}
                    hasArrow
                    placement="top"
                  >
                    <Badge
                      bg="blue.100"
                      color="blue.800"
                      fontSize="0.65rem"
                      fontWeight="800"
                      px={2}
                      h="20px"
                      minH="20px"
                      borderRadius="md"
                      textTransform="uppercase"
                      display="inline-flex"
                      alignItems="center"
                      justifyContent="center"
                      cursor="pointer"
                      lineHeight="1"
                    >
                      <BiStore size={13} />
                    </Badge>
                  </Tooltip>
                )}
              </HStack>
            </Box>

            <Box display="flex" justifyContent="flex-end" pl={4}>
              <VStack spacing={1} align="flex-end">
                {option.deliveryCostDiscountPercentage ? (
                  <HStack spacing={2} align="center">
                    <Box position="relative">
                      <Text
                        fontSize="0.8rem"
                        color="gray.500"
                        fontFamily="'Nunito','Inter', sans-serif"
                        fontWeight="600"
                      >
                        ₹{effectiveDeliveryCost}
                      </Text>
                      <Box
                        position="absolute"
                        top="50%"
                        left="-10%"
                        right="-10%"
                        height="1.2px"
                        bg="gray.500"
                        transform="rotate(-12deg)"
                        transformOrigin="center"
                        pointerEvents="none"
                      />
                    </Box>
                    <Text
                      fontSize="0.85rem"
                      color="gray.800"
                      fontFamily="'Nunito','Inter', sans-serif"
                      fontWeight="800"
                      lineHeight="1"
                    >
                      ₹
                      {calculateDiscountedPrice(
                        effectiveDeliveryCost,
                        option.deliveryCostDiscountPercentage,
                      )}
                    </Text>
                  </HStack>
                ) : (
                  <Text
                    fontSize="0.85rem"
                    color="gray.800"
                    fontFamily="'Nunito','Inter', sans-serif"
                    fontWeight="700"
                    lineHeight="1"
                  >
                    ₹ {effectiveDeliveryCost}
                  </Text>
                )}
              </VStack>
            </Box>
          </Flex>
        </VStack>
      </HStack>
      {!isRemainingItems && !isReadOnly && canDelete && (
        <Box
          w={DELETE_COLUMN_WIDTH}
          flexShrink={0}
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
        >
          <DeleteOptionControl
            isDisabled={isDeleteDisabled}
            disabledTooltip={deleteDisabledTooltip}
            isDeleting={isDeletingOption}
            onConfirmDelete={() => onDelete(option._id)}
          />
        </Box>
      )}
    </HStack>
  );
};
